import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { borderStyle } from "../../styles/consts";
import PopUpBox from "../components/PopUpBox";

interface Task {
  description: string;
  label: string;
}

interface ProjectInfo {
  Role: string;
  Project: string;
  Goal: string;
  "Time period": string;
  Tasks: Task[];
  Technologies: string[];
}

interface Project {
  title: string;
  info: ProjectInfo;
}

interface AddProjectProps {
  onSave: (project: Project) => Promise<void>;
  disabled?: boolean;
}

export default function AddProject({ onSave, disabled }: AddProjectProps) {
  const [title, setTitle] = useState("");
  const [role, setRole] = useState("");
  const [project, setProject] = useState("");
  const [goal, setGoal] = useState("");
  const [timePeriod, setTimePeriod] = useState("");
  const [tasks, setTasks] = useState<Task[]>([{ description: "", label: "" }]);
  const [technologies, setTechnologies] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [closePopUp, setClosePopUp] = useState(false);

  const handleAddTask = () => {
    setTasks([...tasks, { description: "", label: "" }]);
  };

  const handleRemoveTask = (index: number) => {
    if (tasks.length > 1) {
      setTasks(tasks.filter((_, i) => i !== index));
    }
  };

  const handleTaskChange = (
    index: number,
    field: "description" | "label",
    value: string
  ) => {
    const newTasks = [...tasks];
    newTasks[index][field] = value;
    setTasks(newTasks);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const projectData: Project = {
        title,
        info: {
          Role: role,
          Project: project,
          Goal: goal,
          "Time period": timePeriod,
          Tasks: tasks.filter((t) => t.description && t.label),
          Technologies: technologies
            .split(",")
            .map((tech) => tech.trim())
            .filter((tech) => tech),
        },
      };

      await onSave(projectData);

      setTitle("");
      setRole("");
      setProject("");
      setGoal("");
      setTimePeriod("");
      setTasks([{ description: "", label: "" }]);
      setTechnologies("");

      setClosePopUp(true);
    } catch (error) {
      console.error("Error saving project:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setTitle("");
    setRole("");
    setProject("");
    setGoal("");
    setTimePeriod("");
    setTasks([{ description: "", label: "" }]);
    setTechnologies("");
  };

  const hasValidData = () => {
    return (
      title.trim() &&
      role.trim() &&
      project.trim() &&
      goal.trim() &&
      timePeriod.trim() &&
      tasks.some((t) => t.description.trim() && t.label.trim()) &&
      technologies.trim()
    );
  };

  return (
    <PopUpBox
      button={
        <Button
          variant="outlined"
          disabled={disabled}
          sx={{
            "&&": {
              borderBottom: "none",
              borderTop: "none",
              borderRight: "none",
            },
            ":disabled": {
              borderLeft: borderStyle,
            },
          }}
        >
          + Add Project
        </Button>
      }
      heading="Add New Project"
      closePopUp={closePopUp}
      setClosePopUp={setClosePopUp}
      onClose={handleClose}
    >
      <Stack
        spacing={2}
        sx={{
          minWidth: "650px",
          maxHeight: "70vh",
          overflow: "auto",
          padding: "0.5rem",
        }}
      >
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              marginBottom: "0.75rem",
              fontWeight: 500,
              color: "#333",
            }}
          >
            Project Title *
          </Typography>
          <TextField
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., AI Tool Platform @Perelyn (Technology Sector)"
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#f9f9f9",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
                "&.Mui-focused": {
                  backgroundColor: "#fff",
                },
              },
            }}
          />
        </Box>

        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              marginBottom: "0.75rem",
              fontWeight: 500,
              color: "#333",
            }}
          >
            Role *
          </Typography>
          <TextField
            fullWidth
            value={role}
            onChange={(e) => setRole(e.target.value)}
            placeholder="e.g., Senior Full-stack AI Software Engineer"
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#f9f9f9",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
                "&.Mui-focused": {
                  backgroundColor: "#fff",
                },
              },
            }}
          />
        </Box>

        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              marginBottom: "0.75rem",
              fontWeight: 500,
              color: "#333",
            }}
          >
            Project *
          </Typography>
          <TextField
            fullWidth
            value={project}
            onChange={(e) => setProject(e.target.value)}
            placeholder="e.g., AI/LLM Backed use case tool platform"
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#f9f9f9",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
                "&.Mui-focused": {
                  backgroundColor: "#fff",
                },
              },
            }}
          />
        </Box>

        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              marginBottom: "0.75rem",
              fontWeight: 500,
              color: "#333",
            }}
          >
            Goal *
          </Typography>
          <TextField
            fullWidth
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            placeholder="e.g., Developing and implementing infrastructure (Backend/Frontend)"
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#f9f9f9",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
                "&.Mui-focused": {
                  backgroundColor: "#fff",
                },
              },
            }}
          />
        </Box>

        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              marginBottom: "0.75rem",
              fontWeight: 500,
              color: "#333",
            }}
          >
            Time Period *
          </Typography>
          <TextField
            fullWidth
            value={timePeriod}
            onChange={(e) => setTimePeriod(e.target.value)}
            placeholder="e.g., 06/2024 - present"
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#f9f9f9",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
                "&.Mui-focused": {
                  backgroundColor: "#fff",
                },
              },
            }}
          />
        </Box>

        <Box>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ marginBottom: "0.75rem" }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 500,
                color: "#333",
              }}
            >
              Tasks *
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddTask}
              sx={{
                textTransform: "none",
                fontSize: "0.875rem",
              }}
            >
              Add Task
            </Button>
          </Stack>

          <Stack spacing={1.5}>
            {tasks.map((task, index) => (
              <Box
                key={index}
                sx={{
                  padding: "1rem",
                  backgroundColor: "#f9f9f9",
                  borderRadius: "4px",
                  border: "1px solid #e0e0e0",
                }}
              >
                <Stack spacing={1}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 500, color: "#666" }}
                    >
                      Task {index + 1}
                    </Typography>
                    {tasks.length > 1 && (
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveTask(index)}
                        sx={{ padding: "4px" }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                  <TextField
                    fullWidth
                    size="small"
                    value={task.label}
                    onChange={(e) =>
                      handleTaskChange(index, "label", e.target.value)
                    }
                    placeholder="Task label (e.g., Backend development)"
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#fff",
                      },
                    }}
                  />
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                    value={task.description}
                    onChange={(e) =>
                      handleTaskChange(index, "description", e.target.value)
                    }
                    placeholder="Task description"
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#fff",
                      },
                    }}
                  />
                </Stack>
              </Box>
            ))}
          </Stack>
        </Box>

        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              marginBottom: "0.75rem",
              fontWeight: 500,
              color: "#333",
            }}
          >
            Technologies *
          </Typography>
          <TextField
            multiline
            fullWidth
            rows={4}
            value={technologies}
            onChange={(e) => setTechnologies(e.target.value)}
            placeholder="Enter technologies separated by commas (e.g., Python, React, AWS, Docker)"
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#f9f9f9",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
                "&.Mui-focused": {
                  backgroundColor: "#fff",
                },
              },
            }}
          />
          <Typography
            variant="caption"
            sx={{
              mt: 1,
              display: "block",
              color: "#666",
              backgroundColor: "#f0f0f0",
              padding: "0.5rem",
              borderRadius: "4px",
            }}
          >
            Tip: Separate technologies with commas. They will be saved as a
            list.
          </Typography>
        </Box>

        <Stack
          direction="row"
          spacing={2}
          justifyContent="flex-end"
          sx={{
            marginTop: "1rem",
            paddingTop: "1rem",
            borderTop: "1px solid #e0e0e0",
          }}
        >
          <Button
            variant="outlined"
            onClick={() => setClosePopUp(true)}
            disabled={saving}
            sx={{
              padding: "0.5rem 1.5rem",
              textTransform: "none",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!hasValidData() || saving}
            sx={{
              padding: "0.5rem 1.5rem",
              textTransform: "none",
            }}
          >
            {saving ? "Saving..." : "Save Project"}
          </Button>
        </Stack>
      </Stack>
    </PopUpBox>
  );
}
